import { useState, useEffect, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { fileUrl, formatPrice } from "@/lib/api";
import { buildWaUrl } from "@/lib/whatsapp";
import { Minus, Plus, Trash2, ShoppingBag, MapPin, Banknote, CreditCard } from "lucide-react";
import { toast } from "sonner";

export const CartDrawer = ({ open, onClose, items, setQty, removeItem, clearCart, settings, zones = [] }) => {
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [payMethod, setPayMethod] = useState("cash");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const skipFetch = useRef(false);

  useEffect(() => {
    setAddress(localStorage.getItem("atelier_customer_address") || "");
  }, [open]);

  // Address autocomplete via the French government Base Adresse Nationale API (free, no key).
  useEffect(() => {
    if (skipFetch.current) {
      skipFetch.current = false;
      return;
    }
    const q = address.trim();
    if (q.length < 3) {
      setSuggestions([]);
      return;
    }
    const ctrl = new AbortController();
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(q)}&limit=5`,
          { signal: ctrl.signal }
        );
        const data = await res.json();
        setSuggestions((data.features || []).map((f) => f.properties.label));
      } catch (e) {
        // network/abort -> ignore
      }
    }, 300);
    return () => {
      clearTimeout(timer);
      ctrl.abort();
    };
  }, [address]);

  const selectSuggestion = (label) => {
    skipFetch.current = true;
    setAddress(label);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  // Delivery zone check based on the postal code found in the address.
  const postal = (address.match(/\b\d{5}\b/g) || []).pop() || "";
  const activeZones = zones.filter((z) => z.active);
  const zonesConfigured = activeZones.length > 0;
  const matchedZone = activeZones
    .filter((z) => postal && postal.startsWith((z.postal_prefix || "").trim()))
    .sort((a, b) => (b.postal_prefix || "").length - (a.postal_prefix || "").length)[0];
  const deliveryAllowed = !zonesConfigured || !!matchedZone;
  const deliveryFee = matchedZone ? Number(matchedZone.fee || 0) : 0;
  const grandTotal = total + deliveryFee;

  const handleOrder = () => {
    if (!address.trim()) {
      toast.error("Veuillez indiquer l'adresse de livraison pour valider.");
      return;
    }
    if (items.length === 0) return;
    if (!deliveryAllowed) {
      toast.error("Désolé, nous ne livrons pas encore à cette adresse.");
      return;
    }
    localStorage.setItem("atelier_customer_address", address.trim());

    // Build + open WhatsApp synchronously (inside the click gesture).
    const { url, phone } = buildWaUrl({
      items,
      address: address.trim(),
      notes: notes.trim(),
      settings,
      payment: { method: payMethod },
      deliveryFee,
    });
    if (!phone) {
      toast.error("Numéro WhatsApp du vendeur non configuré.");
      return;
    }
    const win = window.open(url, "_blank");
    if (!win) window.location.href = url;

    api
      .post("/orders", {
        delivery_address: address.trim(),
        notes: notes.trim(),
        items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, quantity: i.quantity })),
        total: grandTotal,
        payment_method: payMethod,
        payment_status: "n/a",
      })
      .catch(() => {});

    toast.success("Commande envoyée sur WhatsApp !");
    clearCart();
    onClose();
  };


  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()}>
      <SheetContent
        data-testid="cart-drawer"
        className="w-full sm:max-w-md flex flex-col p-0 gap-0"
      >
        <SheetHeader className="p-5 border-b border-border">
          <SheetTitle className="font-heading text-2xl flex items-center gap-2">
            <ShoppingBag className="h-5 w-5 text-terracotta" />
            Mon panier
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {items.length === 0 ? (
            <div className="text-center text-muted-foreground py-16">
              <ShoppingBag className="h-10 w-10 mx-auto mb-3 opacity-40" />
              Votre panier est vide.
            </div>
          ) : (
            items.map((i) => (
              <div key={i.id} className="flex gap-3" data-testid={`cart-item-${i.id}`}>
                <div className="h-16 w-16 rounded-xl overflow-hidden bg-muted shrink-0">
                  {i.image_url && (
                    <img src={fileUrl(i.image_url)} alt={i.name} className="h-full w-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm leading-tight truncate">{i.name}</p>
                  <p className="text-terracotta font-bold text-sm">{formatPrice(i.price)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <button
                      data-testid={`cart-decrease-${i.id}`}
                      onClick={() => setQty(i.id, i.quantity - 1)}
                      className="h-7 w-7 rounded-full border border-border flex items-center justify-center hover:bg-muted"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="text-sm font-semibold w-5 text-center">{i.quantity}</span>
                    <button
                      data-testid={`cart-increase-${i.id}`}
                      onClick={() => setQty(i.id, i.quantity + 1)}
                      className="h-7 w-7 rounded-full border border-border flex items-center justify-center hover:bg-muted"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <button
                      data-testid={`cart-remove-${i.id}`}
                      onClick={() => removeItem(i.id)}
                      className="ml-auto text-muted-foreground hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-border p-5 space-y-3 bg-card">
            <div>
              <Label htmlFor="cust-addr" className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" /> Adresse de livraison *
              </Label>
              <div className="relative">
                <Textarea
                  id="cust-addr"
                  data-testid="delivery-address-input"
                  value={address}
                  onChange={(e) => {
                    setAddress(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                  placeholder="Commencez à taper : numéro, rue, ville…"
                  className="mt-1 resize-none"
                  rows={2}
                  autoComplete="off"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <ul
                    data-testid="address-suggestions"
                    className="absolute z-50 left-0 right-0 bottom-full mb-1 bg-popover border border-border rounded-xl shadow-xl overflow-hidden max-h-56 overflow-y-auto"
                  >
                    {suggestions.map((s, i) => (
                      <li key={i}>
                        <button
                          type="button"
                          data-testid={`address-suggestion-${i}`}
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => selectSuggestion(s)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-muted flex items-start gap-2"
                        >
                          <MapPin className="h-4 w-4 mt-0.5 text-terracotta shrink-0" />
                          <span>{s}</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <Textarea
              data-testid="order-notes-input"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Notes (optionnel)"
              className="resize-none"
              rows={1}
            />

            <div className="pt-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Mode de paiement</p>
              <div className="grid grid-cols-2 gap-2">
                <button
                  data-testid="pay-cash-button"
                  onClick={() => setPayMethod("cash")}
                  className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm font-semibold transition-colors ${
                    payMethod === "cash"
                      ? "border-transparent brand-terracotta text-white"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                >
                  <Banknote className="h-4 w-4" /> Espèces
                </button>
                <button
                  data-testid="pay-card-button"
                  onClick={() => setPayMethod("card")}
                  className={`flex items-center justify-center gap-2 rounded-xl border py-3 text-sm font-semibold transition-colors ${
                    payMethod === "card"
                      ? "border-transparent brand-terracotta text-white"
                      : "border-border bg-card hover:bg-muted"
                  }`}
                >
                  <CreditCard className="h-4 w-4" /> Carte bleue
                </button>
              </div>
            </div>

            <div className="space-y-1 pt-1">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Sous-total ({count} article{count > 1 ? "s" : ""})</span>
                <span className="font-semibold">{formatPrice(total)}</span>
              </div>
              {deliveryAllowed && (
                <div className="flex items-center justify-between text-sm" data-testid="delivery-fee-line">
                  <span className="text-muted-foreground">Livraison{matchedZone?.label ? ` · ${matchedZone.label}` : ""}</span>
                  <span className="font-semibold">{deliveryFee > 0 ? formatPrice(deliveryFee) : "Offerte"}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="text-muted-foreground text-sm">Total</span>
                <span className="font-heading text-2xl font-bold" data-testid="cart-grand-total">
                  {formatPrice(grandTotal)}
                </span>
              </div>
            </div>

            {!deliveryAllowed && (
              <div
                data-testid="out-of-zone-warning"
                className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive text-sm px-3 py-2"
              >
                Désolé, nous ne livrons pas encore à cette adresse. Vérifiez votre code postal ou contactez-nous.
              </div>
            )}

            <button
              data-testid="order-whatsapp-button"
              onClick={handleOrder}
              disabled={!deliveryAllowed}
              className="wa-green text-white w-full h-13 py-3.5 rounded-full font-bold text-base flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.149-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Commander via WhatsApp
            </button>
            <p className="text-[11px] text-center text-muted-foreground">
              {payMethod === "card"
                ? "Un message pré-rempli s'ouvrira dans WhatsApp. Paiement par carte."
                : "Un message pré-rempli s'ouvrira dans WhatsApp. Paiement en espèces à la livraison."}
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
